/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { LockRounded } from '@mui/icons-material';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@src/languages';
import Tooltip from '@src/components/Tooltip';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, Divider, Typography } from '@mui/material';
import BadgeAvatar from '@src/components/BadgeAvatar';
import pluralize from '@src/utils/pluralize';
import CustomTypography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import actions from '@src/redux/actions';
import useCustomSSO from '@src/hooks/useCustomSSO';
import useUserInfo from '@src/hooks/useTtsUser';
import apis from '@src/apis';
import {
  checkCustomPackage,
  getDefaultValueCustomResource,
  getPackageName,
} from '@src/services/package';

import { delimitNumber } from '@src/utils/number';
import {
  PAYMENT_HUB_RENEWAL_TYPE,
  SUBSCRIPTION_PURCHASE_STATUS,
} from '@src/constants/subscription';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_EXPIRED,
  PACKAGE_LEVEL,
  PACKAGE_STUDIO_VERSION,
  PACKAGE_TYPE,
} from '@src/constants/package';
import route from '@src/constants/route';
import { VBEE_CONSOLE_URL } from '@src/configs';
import useTotalCredits from '@src/hooks/useTotalCredits';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import { NewUserInformationNoteWrapper } from './index.style';

const checkExpiredDate = (expiryDate) => {
  if (!expiryDate) return false;
  return moment().isAfter(expiryDate);
};

const FORMAT_DATETIME = 'DD/MM/YYYY - HH:mm';

const NewDetailPackageInfo = ({ isShowUpgradeButton }) => {
  const { getFeatureValue } = useFeatureFlags();
  const { keycloak } = useCustomSSO();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { apiLimitRemaining } = useTotalCredits();
  const inListApp = location.pathname === route.LIST_APPS;

  const [usingOrders, setUsingOrders] = useState([]);
  const { user } = useSelector((state) => state.auth);
  const { packageCode } = user;
  const { usingPackage, lastExpiredOrder, subscriptionPurchase } = useSelector(
    (state) => state.user,
  );
  const { id: userId, email, phoneNumber } = user;
  const useInfoCreditsApi = getFeatureValue(FEATURE_KEYS.INFO_CREDITS_API, {
    userId,
    email,
    phoneNumber,
  });

  const {
    remainingCharacters = 0,
    bonusCharacters = 0,
    lockCharacters = 0,
  } = useSelector((state) => state.auth.user);
  const { status } = subscriptionPurchase;
  const { ttsUser } = useUserInfo();
  const { studio } = ttsUser;
  const { cycle, topUp, custom } = studio || {};
  const { remainingCredits: remainingCycleCredits = 0, nextResetAt } =
    cycle || {};
  const { remainingCredits: remainingTopUpCredits = 0 } = topUp || {};
  const { remainingCredits: remainingCustomCredits = 0 } = custom || {};
  const isLockedCycle = ttsUser?.studio?.cycle?.isLocked;
  const isLockedTopUp = ttsUser?.studio?.topUp?.isLocked;
  const isLockedCustom = ttsUser?.studio?.custom?.isLocked;

  const usingStudioOrder = usingOrders?.find(
    (order) => order?.id === user?.orderId,
  );
  const autorenewEnabled = status === SUBSCRIPTION_PURCHASE_STATUS.ACTIVE;
  const isAutoRenewalPackage =
    usingStudioOrder?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW &&
    autorenewEnabled;

  const userUseCustomPackage = useMemo(() => {
    const packageLevel = usingPackage?.level || '';
    return packageLevel === PACKAGE_LEVEL.CUSTOM;
  }, [usingPackage]);
  const { handleGetPackageCodeMapping } = useMappingPackage();

  const { language } = i18n;
  const packageName = usingPackage.id
    ? getPackageName(
        usingPackage.name,
        usingPackage.code,
        language,
        t,
        true,
        true,
      )
    : getPackageName(
        lastExpiredOrder?.package?.name,
        user.packageCode,
        language,
        t,
        true,
        true,
      );

  const { packageExpiryDate } = user;
  const isExpiredPackage = checkExpiredDate(packageExpiryDate);

  const totalOldRemainingCharacters =
    remainingCharacters + bonusCharacters || 0;

  // this below code is the logic to render button
  // clone from src/containers/Subcription/
  // if have time => use redux and refactor as an utils function

  const { type, code, level = '', expiresIn } = usingPackage;

  const isFreePackage = FREE_PACKAGE_CODES.includes(code);
  const isCustomPackage = checkCustomPackage({ type, level });

  const userUseOneTimePackage = useMemo(() => {
    const packageVersion = usingPackage?.version || PACKAGE_STUDIO_VERSION.V1;
    return (
      [
        PACKAGE_STUDIO_VERSION.V1,
        PACKAGE_STUDIO_VERSION.V2_1,
        PACKAGE_STUDIO_VERSION.V2,
      ].includes(packageVersion) && !isFreePackage
    );
  }, [usingPackage, isFreePackage]);

  const {
    defaultCredits = 0,
    defaultDevices = 0,
    defaultVoiceClonings = 0,
  } = getDefaultValueCustomResource(usingPackage?.customResource) || {};

  const handleRedirectToBuyPackage = () => {
    const packageCodeMapping = handleGetPackageCodeMapping({
      level,
      type,
      expiresIn,
    });
    switch (type) {
      case PACKAGE_TYPE.STUDIO:
        // eslint-disable-next-line no-case-declarations
        let redirectRoute = route.PAYMENT;

        redirectRoute = isFreePackage
          ? redirectRoute
          : `${route.PAYMENT}?package=${packageCodeMapping}`;

        redirectRoute = isCustomPackage
          ? `${route.PAYMENT}?package=${packageCodeMapping}&credits=${defaultCredits}&devices=${defaultDevices}&voiceClonings=${defaultVoiceClonings}`
          : redirectRoute;
        history.push(redirectRoute);
        dispatch(actions.layout.handleChangeOpenSidebar(false));
        break;
      case PACKAGE_TYPE.API:
        window.open(
          isFreePackage
            ? `${VBEE_CONSOLE_URL}/payments`
            : `${VBEE_CONSOLE_URL}/payments?package=${code}`,
        );
        dispatch(actions.layout.handleChangeOpenSidebar(false));
        break;
      case PACKAGE_TYPE.DUBBING:
        history.push(
          isFreePackage
            ? route.PAYMENT_DUBBING
            : `${route.PAYMENT_DUBBING}?package=${code}`,
        );
        dispatch(actions.layout.handleChangeOpenSidebar(false));
        break;

      default:
        break;
    }
  };

  const handleNavigateRegisterAutoRenew = () => {
    if (type === PACKAGE_TYPE.STUDIO) {
      history.push(route.SUBSCRIPTION);
      dispatch(actions.layout.handleChangeOpenSidebar(false));
    }
  };

  const renderPackageButton = () => {
    const enableCancelAutoRenew =
      isAutoRenewalPackage &&
      subscriptionPurchase?.status &&
      subscriptionPurchase?.status !== SUBSCRIPTION_PURCHASE_STATUS.CANCELED;

    const enableRegisterAutoRenew =
      isAutoRenewalPackage &&
      (!subscriptionPurchase?.status ||
        subscriptionPurchase?.status === SUBSCRIPTION_PURCHASE_STATUS.CANCELED);

    if (enableCancelAutoRenew) return <></>;

    if (isAutoRenewalPackage && isExpiredPackage) {
      return (
        <Button className="button-upgrade" onClick={handleRedirectToBuyPackage}>
          {t('upgradeNow')}
        </Button>
      );
    }

    if (enableRegisterAutoRenew) {
      return (
        <Button
          onClick={handleNavigateRegisterAutoRenew}
          className="button-upgrade"
        >
          {t('registerAutoRenew')}
        </Button>
      );
    }

    if (isFreePackage || isExpiredPackage) {
      return (
        <Button className="button-upgrade" onClick={handleRedirectToBuyPackage}>
          {t('upgradeNow')}
        </Button>
      );
    }

    return (
      <Button className="button-upgrade" onClick={handleRedirectToBuyPackage}>
        {t('buyMore')}
      </Button>
    );
  };

  const getPackageExpired = () => {
    const isOldPackageVersion =
      packageCode?.includes(PACKAGE_STUDIO_VERSION.V1) ||
      packageCode?.includes(PACKAGE_STUDIO_VERSION.V2) ||
      packageCode?.includes(PACKAGE_STUDIO_VERSION.V2_1);

    if (isExpiredPackage && !isOldPackageVersion) {
      return `${t('exp')}: ${t('expired')}`;
    }
    if (
      isExpiredPackage &&
      !packageCode?.includes('V3') &&
      lockCharacters === 0
    ) {
      return t('outOfDate');
    }
    if (!packageExpiryDate && packageCode) {
      return `${t('exp')}: ${t('endless')}`;
    }
    if (packageExpiryDate) {
      return `${isAutoRenewalPackage ? t('autoRenewal') : t('exp')}: ${moment(
        packageExpiryDate,
      ).format(FORMAT_DATETIME)}`;
    }
    return '';
  };

  const formattedNextResetAt = moment(isFreePackage ? undefined : nextResetAt)
    .add(1, 'days')
    .format('DD/MM/YYYY');

  const monthPackage = usingPackage?.expiresIn === PACKAGE_EXPIRED.MONTHLY;
  const hasOneTimePackage = !userUseOneTimePackage && remainingCharacters > 0;
  const usingCyclePackage =
    !isFreePackage && !userUseOneTimePackage && !userUseCustomPackage;
  const hasOneTimeAndCyclePackage =
    (hasOneTimePackage && usingCyclePackage) ||
    (userUseCustomPackage && hasOneTimePackage);

  const renderIconTooltipLockCredits = () => {
    const titleTooltip = t('contentTooltipLockCredits');
    if (
      (isLockedCycle && remainingCycleCredits) ||
      (isLockedCustom && remainingCustomCredits)
    )
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={titleTooltip}
          placement="left"
        >
          <LockRoundedIcon sx={{ fontSize: 16, color: COLOR.textRejected }} />
        </Tooltip>
      );

    if (isLockedTopUp && remainingTopUpCredits)
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={titleTooltip}
          placement="left"
        >
          <LockRoundedIcon sx={{ fontSize: 16, color: COLOR.textRejected }} />
        </Tooltip>
      );

    return null;
  };

  const getPriceCyclePackage = () => {
    let total = remainingCycleCredits + remainingTopUpCredits;
    if (inListApp && useInfoCreditsApi && !userUseCustomPackage) {
      total = apiLimitRemaining;
    }
    if (isExpiredPackage) {
      return `0 ${pluralize(0, t('credit'), language)}`;
    }
    return `${delimitNumber(total, language)} ${pluralize(
      total,
      t('credit'),
      language,
    )}`;
  };

  useEffect(() => {
    const fetchUsingOrders = async () => {
      const data = await apis.orders.getUsingOrders();
      if (data?.status) setUsingOrders(data.result);
    };
    fetchUsingOrders();
  }, []);

  // Title Cycle Package V3.3
  const getTitleCyclePackage = () => {
    if (inListApp && useInfoCreditsApi && !userUseCustomPackage) {
      return t('apiLimitTotal');
    }

    if (monthPackage && !isAutoRenewalPackage) {
      return t('remainingByCharacters');
    }

    return t('remainCycleCharacters');
  };

  const getTextOldPackageWarning = () => {
    if (inListApp && useInfoCreditsApi) {
      return t('oldPackageV2Warning');
    }
    if (hasOneTimeAndCyclePackage) {
      return t('charactersFromTheOldPlanUsedForAdvancedVoices');
    }

    return t('theseCharactersCanNotBeUsedForAdvancedVoices');
  };

  return (
    <NewUserInformationNoteWrapper>
      <div className="header-user-info">
        <div className="avatar">
          <BadgeAvatar
            width={32}
            img={user.avatar}
            active={keycloak && keycloak.authenticated}
            name={user.firstName || user.lastName}
            number={new Date(user.createdAt)}
            smallImgWidth={20}
            type="icon"
            removeDefaultIcon
          />
        </div>
        <div className="user-info-wrapper">
          <CustomTypography
            variant="body-12-regular"
            color={COLOR.descriptionUpload}
          >
            {`${user?.firstName || ''} ${user?.lastName || ''}`}
          </CustomTypography>
          <CustomTypography
            variant="body-12-regular"
            color={COLOR.descriptionUpload}
          >
            {packageName} |{' '}
            <CustomTypography
              variant="body-12-regular"
              color={
                isExpiredPackage ? COLOR.textRejected : COLOR.descriptionUpload
              }
            >
              {getPackageExpired()}
            </CustomTypography>
          </CustomTypography>
        </div>
      </div>
      <Box className="package-info-container">
        {/* User mới đang dùng gói free */}
        {isFreePackage && !userUseOneTimePackage && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'flex-start',
            }}
          >
            <Box className="cycle-characters">
              <CustomTypography
                variant="body-12-regular"
                color={COLOR.descriptionUpload}
                className="cycle-characters-note"
              >
                {t('remainingByCharacters')}:
              </CustomTypography>
              <Box>
                {!(inListApp && useInfoCreditsApi) ? (
                  <CustomTypography
                    variant="body-12-regular"
                    color={COLOR.descriptionUpload}
                    style={{
                      fontStyle: 'italic',
                    }}
                  >
                    {t('resetAtTheBeginningOfTheDay')} {formattedNextResetAt}
                  </CustomTypography>
                ) : (
                  <CustomTypography
                    variant="body-12-regular"
                    color={COLOR.descriptionUpload}
                    style={{
                      fontStyle: 'italic',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: t('noteFreePackageAPI'),
                    }}
                  />
                )}
              </Box>
            </Box>

            {!(inListApp && useInfoCreditsApi) && (
              <CustomTypography variant="body-14-bold" color={COLOR.darkGray}>
                {user?.bonusCharacters?.toLocaleString(language) || 0}{' '}
                {pluralize(
                  user?.bonusCharacters?.toLocaleString(language),
                  t('credit'),
                  language,
                )}
              </CustomTypography>
            )}
          </div>
        )}

        {/* Đang dùng gói chu kỳ */}
        {usingCyclePackage && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'flex-start',
            }}
          >
            <Box className="cycle-characters">
              <CustomTypography
                variant="body-12-regular"
                color={COLOR.descriptionUpload}
                className="cycle-characters-note"
              >
                {getTitleCyclePackage()}
              </CustomTypography>
              {!isExpiredPackage && (!monthPackage || isAutoRenewalPackage) && (
                <Box>
                  <CustomTypography
                    variant="body-12-regular"
                    color={COLOR.descriptionUpload}
                    style={{
                      fontStyle: 'italic',
                    }}
                  >
                    {t('resetAtTheBeginningOfTheDay')} {formattedNextResetAt}
                  </CustomTypography>
                </Box>
              )}
            </Box>

            <CustomTypography
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
              }}
              variant="body-14-bold"
              color={isExpiredPackage ? COLOR.textRejected : COLOR.darkGray}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                {getPriceCyclePackage()}
                {renderIconTooltipLockCredits()}
              </div>
              {renderIconTooltipLockCredits() && (
                <CustomTypography
                  variant="body-12-regular"
                  color={COLOR.descriptionUpload}
                  style={{
                    fontStyle: 'italic',
                  }}
                >
                  {t('lockCycleCreditsTooltipMyPackage')}
                </CustomTypography>
              )}
            </CustomTypography>
          </div>
        )}

        {/* if user use custom package */}
        {userUseCustomPackage && (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'flex-start',
            }}
          >
            <CustomTypography
              variant="body-12-regular"
              color={COLOR.descriptionUpload}
              className="cycle-characters-note"
            >
              {useInfoCreditsApi && inListApp
                ? t('remainingAPILimit')
                : t('remainingByCharacters')}
              :
            </CustomTypography>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                <CustomTypography
                  variant="body-14-bold"
                  color={isExpiredPackage ? COLOR.textRejected : COLOR.darkGray}
                  className="remain-custom-characters"
                >
                  {`${delimitNumber(
                    remainingCustomCredits,
                    language,
                  )} ${pluralize(
                    remainingCustomCredits,
                    t('credit'),
                    language,
                  )}`}
                </CustomTypography>
                {renderIconTooltipLockCredits()}
              </div>
              {renderIconTooltipLockCredits() && (
                <CustomTypography
                  variant="body-12-regular"
                  color={COLOR.descriptionUpload}
                  style={{
                    fontStyle: 'italic',
                  }}
                >
                  {t('lockCycleCreditsTooltipMyPackage')}
                </CustomTypography>
              )}
            </div>
          </Box>
        )}

        {/* if user have V3 package and remainingCharacter */}
        {/* if user have old package and valid remainingChracters */}
        {((!userUseOneTimePackage &&
          remainingCharacters > 0 &&
          !isExpiredPackage) ||
          (userUseOneTimePackage && !isExpiredPackage)) && (
          <>
            {hasOneTimeAndCyclePackage && <Divider />}
            <div>
              <Box
                className="old-characters"
                style={{
                  display: 'flex',
                  gap: '8px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  <CustomTypography
                    variant="body-12-regular"
                    color={COLOR.descriptionUpload}
                  >
                    {hasOneTimeAndCyclePackage
                      ? t('oldPackageCharacters')
                      : t('remainingByCharacters')}
                    :
                  </CustomTypography>
                  {(usingCyclePackage || userUseCustomPackage) && (
                    <Box className="package-info">
                      <CustomTypography
                        variant="body-12-regular"
                        color={
                          isExpiredPackage
                            ? COLOR.textRejected
                            : COLOR.descriptionUpload
                        }
                      >
                        {getPackageExpired()}
                      </CustomTypography>
                    </Box>
                  )}
                  <Box>
                    <CustomTypography
                      variant="body-12-regular"
                      color={COLOR.descriptionUpload}
                      style={{
                        fontStyle: 'italic',
                      }}
                    >
                      {getTextOldPackageWarning()}
                    </CustomTypography>
                  </Box>
                </div>
                {(!inListApp || !useInfoCreditsApi) && (
                  <CustomTypography
                    variant="body-14-bold"
                    color={COLOR.darkGray}
                  >
                    {`${delimitNumber(
                      totalOldRemainingCharacters,
                      language,
                    )} ${pluralize(
                      totalOldRemainingCharacters,
                      t('credit'),
                      language,
                    )}`}
                  </CustomTypography>
                )}
              </Box>
            </div>
          </>
        )}

        {/* if user have old package and expired */}
        {userUseOneTimePackage && isExpiredPackage && (
          <>
            <Box className="old-characters">
              <Typography className="remain-old-characters">
                {`${delimitNumber(lockCharacters, language)} ${pluralize(
                  lockCharacters,
                  t('credit'),
                  language,
                )}`}
              </Typography>
              <Typography className="old-characters-note">
                {lockCharacters > 0 && (
                  <LockRounded sx={{ fontSize: 16 }} className="lock-icon" />
                )}
              </Typography>
            </Box>

            {lockCharacters > 0 && (
              <Typography className="preserver-date">{`${t(
                'preservedCharactersDateV2',
              )}: ${moment(user?.preservationExpiryDate).format(
                'DD/MM/YYYY - HH:mm',
              )}`}</Typography>
            )}
          </>
        )}
      </Box>
      {isShowUpgradeButton && renderPackageButton()}
    </NewUserInformationNoteWrapper>
  );
};

export default NewDetailPackageInfo;
